import { createCareIndustryPages } from './careIndustryPages.js';
import { createRetailIndustryPages } from './retailIndustryPages.js';
import { createNewIndustryPages } from './industryPageContent.js';
import { createApplicationPage } from './applicationPageContent.js';
import type { StaticPageContent, StaticPageKey } from './staticPageContent.js';
import type { StaticPageDefaultDataDependencies } from './staticPageDefaultDataTypes.js';

export const createStaticPageCompanyDefaults = (
  dependencies: StaticPageDefaultDataDependencies,
): Record<StaticPageKey, StaticPageContent> => {
  const {
    applicationPortal,
    landingPageContentRepository,
    baseContent,
    industryCare,
    industryCraft,
    industryGastro,
    industryRetail,
    groupPhoto,
    partnerGlobus,
    globusGemeinsam,
    globusMarkthalle,
    missionImage,

    globusJobsSection,
    missionStoryContent,
  } = dependencies;
  const pages: Record<StaticPageKey, StaticPageContent> = {
    ...createNewIndustryPages(),
    ...createRetailIndustryPages(),
    ...createCareIndustryPages(),
    bewerben: createApplicationPage(),
    unternehmen: {
      key: 'unternehmen',
      eyebrow: 'Für Unternehmen',
      title: 'Internationale Pflegekräfte gewinnen, ohne Ihr Team mit Bürokratie zu überlasten.',
      intro:
        'Viva Porta unterstützt Kliniken, Pflegeeinrichtungen und Gesundheitsunternehmen beim planbaren Aufbau internationaler Pflegeteams. Wir finden passende Fachkräfte aus der Türkei, prüfen Qualifikation und Motivation, steuern Sprache, Anerkennung, Visum und Einreise und begleiten die Integration bis in den Arbeitsalltag.',
      heroImage: landingPageContentRepository.heroImage,
      cards: [
        {
          iconClass: 'icon-[material-symbols--groups]',
          title: 'Bedarf präzise verstehen',
          text: 'Wir starten mit Ihrer realen Personalsituation: Fachbereiche, Schichtmodelle, Starttermine, Gehaltsrahmen, Wohnsituation und Integrationskapazität. So suchen wir nicht irgendein Profil, sondern die passende Ergänzung für Ihr Team.',
        },
        {
          iconClass: 'icon-[material-symbols--verified-user]',
          title: 'Vorqualifizierte Kandidaten',
          text: 'Sie erhalten nur Profile, die fachlich, sprachlich und persönlich vorgeprüft sind. Ausbildung, Berufserfahrung, Motivation, Deutschlernstand und Anerkennungsfähigkeit werden nachvollziehbar dokumentiert.',
        },
        {
          iconClass: 'icon-[material-symbols--approval-delegation]',
          title: 'Anerkennung und Behörden',
          text: 'Wir koordinieren Dokumente, Übersetzungen, Beglaubigungen, Anerkennungsantrag, Visum und Arbeitserlaubnis. Ihr HR-Team bleibt informiert, muss aber nicht jeden Einzelschritt selbst nachhalten.',
        },
        {
          iconClass: 'icon-[material-symbols--schedule]',
          title: 'Planbare Pipeline',
          text: 'Internationale Gewinnung braucht Vorlauf. Deshalb arbeiten wir mit klaren Meilensteinen, Statusupdates und realistischen Zeitfenstern, damit Dienstplanung und Onboarding früh vorbereitet werden können.',
        },
        {
          iconClass: 'icon-[material-symbols--diversity-3]',
          title: 'Integration mitgedacht',
          text: 'Wohnung, Ankunft, Behördengänge, Teamvorbereitung und kulturelle Orientierung entscheiden über den langfristigen Erfolg. Wir begleiten diese Phase aktiv und bleiben nach dem Start ansprechbar.',
        },
        {
          iconClass: 'icon-[material-symbols--support-agent]',
          title: 'Feste Ansprechpartner',
          text: 'Sie arbeiten mit einem Team, das den deutschen Pflegealltag und die türkische Kandidatenseite versteht. Das reduziert Reibung, beschleunigt Entscheidungen und schafft Vertrauen auf beiden Seiten.',
        },
      ],
    },
    pflege: {
      key: 'pflege',
      eyebrow: 'Branchenlösung Pflege',
      title: 'Pflege braucht Menschen mit Zukunft.',
      intro: 'Du möchtest als Pflegekraft in Deutschland arbeiten? Wir begleiten dich bei Sprache, Anerkennung und dem passenden Einstieg in ein Pflegeteam.',
      heroStageTitle: 'Pflege in Deutschland.',

      heroAction: { label: 'Hier anmelden', href: applicationPortal.pflege },
      heroImage: industryCare,
      heroHighlights: ['Anerkennung begleitet', 'Berufssprache', 'Persönlich betreut'],
      cards: [
        {
          iconClass: 'icon-[material-symbols--medical-services]',
          title: 'Pflegefachkräfte',
          text: 'Viva Porta vermittelt qualifizierte Pflegekräfte an Einrichtungen, die langfristig und fair planen.',
        },
        {
          iconClass: 'icon-[material-symbols--translate]',
          title: 'Sprache und Anerkennung',
          text: 'Der Weg nach Deutschland wird mit Sprachvorbereitung, Unterlagen und Anerkennungsschritten begleitet.',
        },
        {
          iconClass: 'icon-[material-symbols--diversity-1]',
          title: 'Menschlich begleitet',
          text: 'Bewerber:innen und Einrichtungen erhalten feste Ansprechpartner und klare Orientierung.',
        },
      ],
      cta: {
        eyebrow: 'Bewerbung Pflege',
        title: 'Starte deinen Weg mit einer ehrlichen Einschätzung.',
        text: 'Wir prüfen gemeinsam, welche Schritte für Sprache, Anerkennung und Arbeitgebergespräche sinnvoll sind.',
        primaryAction: { label: 'Hier anmelden', href: applicationPortal.pflege },
        secondaryAction: { label: 'Ablauf ansehen', href: '/ablauf' },
      },
    },
    gastro: {
      key: 'gastro',
      eyebrow: 'Branchenlösung Gastro',
      title: 'Dein Start in starken Service-Teams.',
      intro: 'Ob Küche oder Service: Wir helfen dir, ein passendes Team in Deutschland zu finden, und begleiten dich bei Sprache und Vorbereitung.',
      heroStageTitle: 'Dein Start im Service-Team.',

      heroAction: { label: 'Hier anmelden', href: applicationPortal.gastro },
      heroImage: industryGastro,
      heroHighlights: ['Service & Küche', 'Teamfit', 'Sprachliche Vorbereitung'],
      cards: [
        {
          iconClass: 'icon-[material-symbols--restaurant]',
          title: 'Service und Küche',
          text: 'Internationale Arbeitskräfte können Betriebe in Service, Küche, Housekeeping und operativen Rollen stärken.',
        },
        {
          iconClass: 'icon-[material-symbols--event]',
          title: 'Saisonale Planung',
          text: 'Frühzeitige Vorbereitung hilft Betrieben und Bewerber:innen, Startzeitpunkte realistisch zu planen.',
        },
        {
          iconClass: 'icon-[material-symbols--groups]',
          title: 'Teamfit',
          text: 'Erfahrung, Motivation, Sprache und Erwartungen werden sorgfältig abgeglichen.',
        },
      ],
      cta: {
        eyebrow: 'Bewerbung Gastro',
        title: 'Finde heraus, welche Rolle zu dir passt.',
        text: 'Wir klären gemeinsam Erfahrung, Sprachstand, Erwartungen und den realistischen Start in Deutschland.',
        primaryAction: { label: 'Hier anmelden', href: applicationPortal.gastro },
        secondaryAction: { label: 'Ablauf ansehen', href: '/ablauf' },
      },
    },
    handwerk: {
      key: 'handwerk',
      eyebrow: 'Branchenlösung Handwerk',
      title: 'Handwerk braucht Menschen mit Zukunft.',
      intro: 'Dein handwerkliches Können wird in Deutschland gebraucht. Wir schauen mit dir, welcher Betrieb zu deiner Erfahrung passt und wie dein Einstieg gelingt.',
      heroStageTitle: 'Dein Weg ins Handwerk.',

      heroAction: { label: 'Hier anmelden', href: applicationPortal.handwerk },
      heroImage: industryCraft,
      heroHighlights: ['Qualifikation eingeordnet', 'Betrieblich passend', 'Ankommen begleitet'],
      cards: [
        {
          iconClass: 'icon-[material-symbols--construction]',
          title: 'Qualifikation',
          text: 'Praktische Erfahrung und Ausbildungswege werden sauber eingeordnet.',
        },
        {
          iconClass: 'icon-[material-symbols--handshake]',
          title: 'Betrieb und Mensch',
          text: 'Kleine Teams brauchen Passung, Vertrauen und klare Erwartungen auf beiden Seiten.',
        },
        {
          iconClass: 'icon-[material-symbols--home-work]',
          title: 'Ankommen',
          text: 'Der berufliche Start wird zusammen mit Alltag, Wohnen und Integration gedacht.',
        },
      ],
      cta: {
        eyebrow: 'Bewerbung Handwerk',
        title: 'Ordne deine Qualifikation für Deutschland ein.',
        text: 'Wir schauen auf Erfahrung, Unterlagen, Sprache und Betriebe, die langfristig planen.',
        primaryAction: { label: 'Hier anmelden', href: applicationPortal.handwerk },
        secondaryAction: { label: 'Ablauf ansehen', href: '/ablauf' },
      },
    },
    einzelhandel: {
      key: 'einzelhandel',
      eyebrow: 'Branchenlösung Einzelhandel',
      title: 'Einzelhandel. Service, Frische und Kundennähe.',
      intro: 'Du arbeitest gern mit Menschen und frischen Produkten? Wir begleiten deinen Einstieg in Verkauf, Beratung oder Frischetheke in Deutschland.',
      heroStageTitle: 'Dein Start im Handel.',

      heroAction: { label: 'Hier anmelden', href: applicationPortal.einzelhandel },
      heroImage: industryRetail,
      heroHighlights: ['Kundenkontakt', 'Frischebereiche', 'Langfristiger Start'],
      cards: [
        {
          iconClass: 'icon-[material-symbols--storefront]',
          title: 'Filialen und Standorte',
          text: 'Bedarf, Arbeitszeiten und Kundenkontakt unterscheiden sich je Standort und werden vorab geklärt.',
        },
        {
          iconClass: 'icon-[material-symbols--support-agent]',
          title: 'Service',
          text: 'Sprachliche und persönliche Vorbereitung ist im Verkauf besonders wichtig.',
        },
        {
          iconClass: 'icon-[material-symbols--verified-user]',
          title: 'Langfristigkeit',
          text: 'Ziel ist ein Start, der beruflich und privat tragfähig ist.',
        },
      ],
      sections: [globusJobsSection()],
      cta: {
        eyebrow: 'Bewerbung Einzelhandel',
        title: 'Bereite deinen Einstieg im Handel vor.',
        text: 'Wir klären Kundenkontakt, Sprache, Standort und Rollen, die zu deinem Alltag passen.',
        primaryAction: { label: 'Hier anmelden', href: applicationPortal.einzelhandel },
        secondaryAction: { label: 'Ablauf ansehen', href: '/ablauf' },
      },
    },
    globus: {
      key: 'globus',
      eyebrow: 'GLOBUS als Partner',
      title: 'GLOBUS und Viva Porta: Personalgewinnung, die zum Markt passt.',
      intro:
        'Die Zusammenarbeit zeigt, wie aus einem konkreten Personalbedarf ein verlässlicher Vermittlungsprozess entsteht: mit passenden Profilen, klarer Vorbereitung und Begleitung bis zum Arbeitsstart.',
      heroImage: globusGemeinsam,
      heroGallery: [
        {
          image: partnerGlobus,
          imageAlt: 'GLOBUS Logo als Zeichen der Partnerschaft',
          kind: 'logo',
        },
        {
          image: globusMarkthalle,
          imageAlt: 'GLOBUS Markthalle als Partner von Viva Porta',
        },
      ],
      cards: [
        {
          iconClass: 'icon-[material-symbols--storefront]',
          title: 'Bedarf nah am Markt',
          text: 'Aus Standort, Schichtmodell, Aufgaben und Teamstruktur entsteht ein klares Suchprofil, das die betriebliche Realität abbildet.',
        },
        {
          iconClass: 'icon-[material-symbols--person-search]',
          title: 'Passende Profile',
          text: 'Viva Porta gleicht Qualifikation, praktische Erfahrung, Sprache, Motivation und Verfügbarkeit mit dem konkreten Bedarf ab.',
        },
        {
          iconClass: 'icon-[material-symbols--handshake]',
          title: 'Begleitung bis zum Start',
          text: 'Auswahl, Vorbereitung, Einreise und Ankommen werden als zusammenhängender Prozess für Unternehmen und Mitarbeitende koordiniert.',
        },
      ],
      sections: [
        {
          eyebrow: 'Case Study GLOBUS',
          title: 'Aus Personalbedarf wird eine belastbare Partnerschaft.',
          text: 'GLOBUS ist ein starker Partner für Viva Porta, weil Anforderungen offen benannt und Einstiege gemeinsam vorbereitet werden. Statt Profile nur weiterzureichen, werden Aufgaben, Erwartungen und Rahmenbedingungen früh geklärt. Das schafft eine fundierte Auswahl und einen planbaren Start für Markt, Team und neue Mitarbeitende.',
          image: globusGemeinsam,
          imageAlt: 'Vertreter von Viva Porta und GLOBUS bei der gemeinsamen Partnerschaft',
        },
        {
          eyebrow: 'Vermittlungsfelder',
          title: 'Qualifizierte Mitarbeitende für Frische, Handwerk und Gastronomie.',
          text: 'Im Mittelpunkt stehen Profile für Bäckerei, Metzgerei, Frischetheke und Gastronomie. Viva Porta prüft die fachliche Erfahrung ebenso wie Sprachstand, Motivation und Teamfit. So wird aus einem allgemeinen Personalbedarf ein Partner-Matching für konkrete Rollen und Standorte.',
          image: partnerGlobus,
          imageAlt: 'GLOBUS Logo als Zeichen der Partnerschaft',
          cards: [
            {
              iconClass: 'icon-[material-symbols--bakery-dining]',
              title: 'Bäckerei',
              text: 'Profile für Produktion, Warenqualität, Abläufe und kundennahen Verkauf.',
            },
            {
              iconClass: 'icon-[material-symbols--set-meal]',
              title: 'Metzgerei',
              text: 'Fachlich passende Mitarbeitende für Herstellung, Zuschnitt, Hygiene und Beratung.',
            },
            {
              iconClass: 'icon-[material-symbols--storefront]',
              title: 'Frischetheke',
              text: 'Serviceorientierte Profile mit Produktverständnis, Sorgfalt und Freude an Beratung.',
            },
            {
              iconClass: 'icon-[material-symbols--restaurant]',
              title: 'Gastronomie',
              text: 'Mitarbeitende für Zubereitung, Ausgabe, Gästekontakt und verlässliche Teamabläufe.',
            },
          ],
        },
      ],
      cta: {
        eyebrow: 'Partner werden',
        title: 'Sie suchen Fachkräfte und möchten mit Viva Porta zusammenarbeiten?',
        text: 'Wir klären Ihren Bedarf, die gesuchten Qualifikationen und den passenden Zeitplan. Über unser Partner-Matching erhalten Sie Zugang zu vorqualifizierten internationalen Profilen und persönlicher Begleitung bis zum Arbeitsstart.',
        image: groupPhoto,
        imageAlt: 'Viva Porta zu Besuch im Metzgereibereich einer GLOBUS Markthalle',
        primaryAction: { label: 'Fachkräfte anfragen', href: '#fachkraefte-anfragen' },
        secondaryAction: { label: 'Partner-Matching ansehen', href: '/ablauf' },
      },
    },
    ablauf: {
      key: 'ablauf',
      eyebrow: 'Ablauf für Unternehmen',
      title: 'Planbar von Bedarf bis Arbeitsstart.',
      intro:
        'Viva Porta macht internationale Personalgewinnung mit klaren Etappen, festen Verantwortlichkeiten und persönlicher Begleitung verlässlich steuerbar.',
      heroImage: globusMarkthalle,
      cards: [
        {
          iconClass: 'icon-[material-symbols--groups]',
          title: 'Kennenlernen und Bedarf',
          text: 'Wir klären Rollen, Anzahl, Qualifikation, Standort, Startkorridor und Rahmenbedingungen. Daraus entsteht ein Suchprofil, das realistisch zur internationalen Gewinnung passt.',
        },
        {
          iconClass: 'icon-[material-symbols--inventory]',
          title: 'Talentpool vorstellen',
          text: 'Sie erhalten einen strukturierten Blick auf passende Profile, Qualifikationen, Sprachstand, Motivation und mögliche Einsatzbereiche.',
        },
        {
          iconClass: 'icon-[material-symbols--manage-search]',
          title: 'Passende Auswahl',
          text: 'Wir identifizieren Kandidatinnen und Kandidaten, prüfen Unterlagen und bereiten die Profile so auf, dass Entscheidungen schnell und nachvollziehbar möglich sind.',
        },
        {
          iconClass: 'icon-[material-symbols--forum]',
          title: 'Gemeinsame Gespräche',
          text: 'Auswahlgespräche werden vorbereitet, moderiert und nachbereitet. Motivation, Kommunikation, Entwicklungspotenzial und Teamfit werden transparent eingeordnet.',
        },
        {
          iconClass: 'icon-[material-symbols--contract-edit]',
          title: 'Arbeitsvertrag',
          text: 'Nach Ihrer Zusage schaffen Vertrag und Zeitplan Verbindlichkeit für beide Seiten und geben Betrieb, Kandidat:in und Lernprozess frühe Orientierung.',
        },
        {
          iconClass: 'icon-[material-symbols--translate]',
          title: 'Sprache und Anerkennung',
          text: 'Digitale Deutschkurse, Fachsprache, Unterlagen, Übersetzungen und Anerkennungsverfahren werden mit Blick auf den Zielstart koordiniert.',
        },
        {
          iconClass: 'icon-[material-symbols--badge]',
          title: 'Behörden und Einreise',
          text: 'Visa, Arbeitserlaubnis, Formalitäten, Anreise und erste Unterkunft werden abgestimmt, damit aus Zusage und Anerkennung ein belastbarer Start wird.',
        },
        {
          iconClass: 'icon-[material-symbols--flight-land]',
          title: 'Integration begleiten',
          text: 'Nach Ankunft bleiben wir ansprechbar: Orientierung, Integrations- und Demokratiekurse, Teamfragen und Alltag werden in der Startphase eng begleitet.',
        },
      ],
    },
    'ueber-vivaporta': {
      key: 'ueber-vivaporta',
      eyebrow: 'Über Viva Porta',
      title: 'Fachkräftegewinnung mit Herkunft, Haltung und Nähe.',
      intro:
        'Viva Porta verbindet Arbeitgeber in Deutschland mit internationalen Fachkräften, weil gute Vermittlung mehr ist als Recruiting: Sie braucht kulturelles Verständnis, saubere Prozesse und Begleitung bis in den Arbeitsalltag.',
      heroImage: missionImage,
      missionStory: missionStoryContent,
      cards: [
        {
          iconClass: 'icon-[material-symbols--diversity-3]',
          title: 'Partnerschaft statt Kurzfristlösung',
          text: 'Wir denken nicht in einzelnen Vakanzen, sondern in belastbaren Personalpfaden. Ziel ist eine Zusammenarbeit, die zu Ihrer Einrichtung, Ihrem Team und Ihren langfristigen Versorgungszielen passt.',
        },
        {
          iconClass: 'icon-[material-symbols--handshake]',
          title: 'Faire Anwerbung',
          text: 'Internationale Vermittlung muss fair für Fachkräfte und verlässlich für Arbeitgeber sein. Deshalb setzen wir auf Transparenz, realistische Erwartungen und persönliche Begleitung auf beiden Seiten.',
        },
        {
          iconClass: 'icon-[material-symbols--support-agent]',
          title: 'Integration als Qualitätsfaktor',
          text: 'Ob eine Fachkraft bleibt, entscheidet sich oft nach der Einreise. Wir unterstützen Arbeitgeber und neue Mitarbeitende in der sensiblen Startphase, damit aus Rekrutierung echte Bindung wird.',
        },
      ],
      sections: [
        {
          eyebrow: 'Unser Hintergrund',
          title: 'Wir übersetzen zwischen Arbeitsmarkt, Anerkennung und Lebensrealität.',
          text: 'Internationale Personalgewinnung bewegt sich zwischen Fachkräftemangel, rechtlichen Anforderungen und sehr persönlichen Entscheidungen. Unser deutsch-türkischer Hintergrund hilft, Erwartungen auf beiden Seiten früh zu klären: Was braucht der Betrieb? Was braucht die Fachkraft? Und was muss vorbereitet sein, damit der Start nicht nur formal, sondern auch menschlich funktioniert?',
          image: groupPhoto,
          imageAlt: 'Viva Porta Team bei einem gemeinsamen Besuch im Arbeitsalltag',
          cards: [
            {
              iconClass: 'icon-[material-symbols--forum]',
              title: 'Kommunikation ohne Umwege',
              text: 'Wir sprechen mit Kandidat:innen, Arbeitgebern und Partnern so, dass Anforderungen, Sorgen und Zusagen transparent bleiben.',
            },
            {
              iconClass: 'icon-[material-symbols--fact-check]',
              title: 'Prozessverständnis',
              text: 'Anerkennung, Sprache, Visum und Einsatzplanung werden nicht getrennt betrachtet, sondern als zusammenhängender Startpfad.',
            },
            {
              iconClass: 'icon-[material-symbols--handshake]',
              title: 'Partnerschaftlich',
              text: 'Wir verstehen Vermittlung als gemeinsame Verantwortung von Fachkraft, Arbeitgeber und Begleitung, nicht als reine Plattformleistung.',
            },
          ],
        },
        {
          eyebrow: 'Warum das zählt',
          title: 'Reichweite findet Profile. Begleitung schafft Bindung.',
          text: 'Deutschland erleichtert internationale Fachkräfteeinwanderung, doch der entscheidende Teil beginnt oft nach der Auswahl: Sprachpraxis, Anerkennungsschritte, Teamkultur, Alltag und verlässliche Ansprechpartner. Viva Porta macht diese Themen früh sichtbar und begleitet sie strukturiert, damit aus einem Personalbedarf ein tragfähiger gemeinsamer Weg wird.',
          cards: [
            {
              iconClass: 'icon-[material-symbols--verified-user]',
              title: 'Fair ausgerichtet',
              text: 'Wir orientieren uns an verantwortungsvollen Leitlinien internationaler Anwerbung und vermeiden Versprechen, die im Alltag nicht belastbar sind.',
            },
            {
              iconClass: 'icon-[material-symbols--school]',
              title: 'Vorbereitet',
              text: 'Sprache, Fachlichkeit und Erwartungsmanagement werden früh miteinander verbunden, damit Auswahl und Start realistisch bleiben.',
            },
            {
              iconClass: 'icon-[material-symbols--support-agent]',
              title: 'Ansprechbar',
              text: 'Nach Vertrag und Einreise bleiben Fragen im Team, bei Behörden und im Alltag Teil der Begleitung.',
            },
          ],
        },
        {
          eyebrow: 'Gemeinsam stark',
          title: 'Viva Porta und Globus: Partnerschaft mit Verantwortung im Alltag.',
          text: 'Mit Globus arbeiten wir an einer Partnerschaft, die internationale Personalgewinnung greifbar macht. Viva Porta bringt strukturierte Vorbereitung, persönliche Begleitung und interkulturelle Erfahrung ein; Globus bietet als etablierte Kette verlässliche Einsatzorte, klare Teams und konkrete Perspektiven. So wird aus Vermittlung ein gemeinsamer Rahmen für Ankommen, Zusammenarbeit und langfristige Bindung.',
          image: globusGemeinsam,
          imageAlt: 'Viva Porta und Globus gemeinsam bei der Partnerschaft',
        },
      ],
    },
    team: {
      key: 'team',
      eyebrow: 'Unser Gründerteam',
      title: 'Ansprechpartner, die beide Seiten der Vermittlung verstehen.',
      intro:
        'Unser Team verbindet Unternehmertum, medizinisches Prozessverständnis, deutsch-türkische Perspektive und operative Nähe zu Kandidaten. Für Arbeitgeber heißt das: kurze Wege, klare Kommunikation und ein realistischer Blick auf internationale Personalgewinnung.',
      cards: [],
      sections: [
        {
          eyebrow: 'Unser Hintergrund',
          title: 'Unser USP: Wir bauen nicht nur Reichweite auf, sondern Vertrauen zwischen Systemen.',
          text: 'Internationale Fachkräftegewinnung ist heute politisch gewollt und rechtlich stärker geöffnet, bleibt in der Praxis aber anspruchsvoll: Anerkennung, Sprache, Visum, Arbeitgebererwartungen, kulturelle Orientierung und Integration greifen ineinander. Viva Porta ist aus genau dieser Lücke entstanden. Wir kombinieren deutsch-türkische Nähe, operatives Kandidatenverständnis und den Blick auf deutsche Arbeitgeberprozesse. Dadurch können wir früher klären, ob ein Profil wirklich passt, welche Vorbereitung nötig ist und welche Begleitung nach der Einreise gebraucht wird.',
          cards: [
            {
              iconClass: 'icon-[material-symbols--diversity-3]',
              title: 'Kulturelle Übersetzung',
              text: 'Wir übersetzen nicht nur Sprache, sondern Erwartungen: Kommunikation im Team, Rollenverständnis, Verbindlichkeit, Rückfragen und Entscheidungswege.',
            },
            {
              iconClass: 'icon-[material-symbols--fact-check]',
              title: 'Prozesssicherheit',
              text: 'Anerkennung, Unterlagen, Sprache und Einreise werden als zusammenhängender Pfad gesteuert. Arbeitgeber erhalten dadurch mehr Transparenz über Status, Risiken und nächste Schritte.',
            },
            {
              iconClass: 'icon-[material-symbols--handshake]',
              title: 'Fairer Anspruch',
              text: 'Internationale Anwerbung braucht Verantwortung gegenüber Herkunfts- und Zielland. Wir arbeiten deshalb mit klaren Informationen, nachvollziehbaren Erwartungen und persönlicher Begleitung.',
            },
            {
              iconClass: 'icon-[material-symbols--groups]',
              title: 'Bindung statt Besetzung',
              text: 'Für Arbeitgeber zählt nicht nur, dass jemand startet, sondern ob die Person bleibt. Unser Team denkt Onboarding, Alltag und Integration deshalb schon vor der Vertragsunterschrift mit.',
            },
          ],
        },
      ],
      team: [...baseContent.team.members],
    },
    kontakt: {
      key: 'kontakt',
      eyebrow: 'Direkter Kontakt',
      title: 'Fachkräfte gesucht? Sprechen wir.',
      intro: 'Sagen Sie uns, wen Sie suchen. Wir planen die nächsten Schritte.',
      cards: [
        {
          iconClass: 'icon-[material-symbols--mail]',
          title: 'E-Mail',
          text: 'info@vivaporta.org',
        },
        {
          iconClass: 'icon-[material-symbols--call]',
          title: 'Telefon',
          text: '+49 176 42887719',
        },
        {
          iconClass: 'icon-[material-symbols--location-on]',
          title: 'Anschrift',
          text: 'Mittelstraße 81, 44575 Castrop-Rauxel',
        },
      ],
      contact: {
        eyebrow: 'Persönliche Beratung',
        title: 'Kontaktieren Sie uns',
        email: 'info@vivaporta.org',
        phone: '+49 176 42887719',
        address: 'Mittelstraße 81, 44575 Castrop-Rauxel',
        text: 'Schildern Sie uns kurz Ihren Bedarf: Fachbereich, Anzahl der gesuchten Pflegekräfte, gewünschter Startzeitraum und besondere Anforderungen. Wir melden uns mit einer realistischen Einschätzung, passenden nächsten Schritten und einem klaren Blick auf Aufwand, Zeitplan und Integration.',
        mapEmbedUrl:
          'https://www.openstreetmap.org/export/embed.html?bbox=7.299%2C51.545%2C7.326%2C51.563&layer=mapnik&marker=51.554%2C7.312',
      },
    },
    impressum: {
      key: 'impressum',
      eyebrow: 'Impressum',
      title: 'Angaben gemäß gesetzlichen Informationspflichten.',
      intro:
        'Hier findest du die Anbieterkennzeichnung und die rechtlichen Pflichtinformationen der Viva Porta GmbH.',
      cards: [
        {
          iconClass: 'icon-[material-symbols--person]',
          title: 'Webseitenbetreiber',
          text: 'Enes Vural',
        },
        {
          iconClass: 'icon-[material-symbols--corporate-fare]',
          title: 'Unternehmen',
          text: 'Gesellschaft mit beschränkter Haftung (GmbH). Verantwortlicher: Enes Vural.',
        },
        {
          iconClass: 'icon-[material-symbols--location-on]',
          title: 'Anschrift',
          text: 'Mittelstraße 81, 44575 Castrop-Rauxel',
        },
        {
          iconClass: 'icon-[material-symbols--alternate-email]',
          title: 'Kontakt',
          text: 'E-Mail: info@vivaporta.org. Telefon: +49 176 42887719.',
        },
        {
          iconClass: 'icon-[material-symbols--fact-check]',
          title: 'Unternehmensregistrierung',
          text: 'HRB 37337, Handelsregister B des Amtsgerichts Dortmund. Umsatzsteuer-Identifikationsnummer: DE455499883.',
        },
        {
          iconClass: 'icon-[material-symbols--gavel]',
          title: 'Online-Streitbeilegung',
          text: 'Die EU-Kommission stellt eine Plattform zur außergerichtlichen Online-Beilegung von Streitigkeiten bereit: https://ec.europa.eu/consumers/odr/. Hinweis gemäß § 36 Verbraucherstreitbeilegungsgesetz (VSBG).',
        },
      ],
    },
  };
  return pages;
};
